"use client"

import { forwardRef, type ComponentPropsWithoutRef, type MouseEvent } from "react"
import { isCapacitorNative } from "@/lib/capacitor"
import { openExternalAuth } from "@/lib/capacitor/oauth-browser"

/** Public website that serves the legal pages; the app bundle has no back button on them. */
export const LEGAL_SITE_ORIGIN = "https://sigmai.football"

export type LegalPath = "/privacy" | "/terms"

type LegalLinkProps = Omit<ComponentPropsWithoutRef<"a">, "href" | "target" | "rel"> & {
  href: LegalPath
}

/**
 * Link to the Privacy Statement or Terms of Use that never leaves the current screen.
 * Web: opens a new tab, so an onboarding dialog keeps its state.
 * App: opens the website in the in-app browser (Done button, own scroll, safe area),
 * instead of navigating the WebView to a landing page with no way back.
 */
export const LegalLink = forwardRef<HTMLAnchorElement, LegalLinkProps>(function LegalLink(
  { href, onClick, children, ...rest },
  ref
) {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event)
    if (event.defaultPrevented || !isCapacitorNative()) return
    event.preventDefault()
    void openExternalAuth(`${LEGAL_SITE_ORIGIN}${href}`).catch(() => {
      window.open(`${LEGAL_SITE_ORIGIN}${href}`, "_blank", "noopener,noreferrer")
    })
  }

  return (
    <a ref={ref} href={href} target="_blank" rel="noopener noreferrer" onClick={handleClick} {...rest}>
      {children}
    </a>
  )
})
